import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { bacenCaseParams } from '@/services/bacen';
import type { BacenFilterOption, QuantileSummary } from '@/services/types';

/** The quantile columns every BRL ladder on the BACEN screens prints. */
export const QUANTILES = ['p10', 'p25', 'p50', 'p75', 'p90'] as const;

export const CNPJ_DIGITS = 14;

/** The case list filtered to one predicate a screen counts by. */
export function bacenCasesHref(filters: Parameters<typeof bacenCaseParams>[0]): string {
  const params = bacenCaseParams(filters).toString();
  return params ? `/bacen/cases?${params}` : '/bacen/cases';
}

/** The three formatters every BACEN screen needs, built once per render. */
export function useFormats() {
  const { i18n } = useTranslation();
  return {
    number: new Intl.NumberFormat(i18n.language),
    currency: new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: 0,
    }),
    percent: new Intl.NumberFormat(i18n.language, {
      style: 'percent',
      maximumFractionDigits: 1,
    }),
  };
}

/**
 * A code from the loader's vocabulary (penalty, status family, appeal kind,
 * trajectory, disposition, vote position...) rendered through its locale
 * entry, falling back to the code itself so a new value is visible rather
 * than blank.
 */
export function useCodeLabel() {
  const { t } = useTranslation(['bacen']);
  return (group: string, code: string | null | undefined): string =>
    code ? t(`bacen:codes.${group}.${code}`, { defaultValue: code }) : '—';
}

/** Title, subtitle, explainer and the export button, the way every screen opens. */
export function PageHeader({
  page,
  exportPath,
  exportLabel,
  children,
}: {
  /** The page's locale block (`bacen:cases`): title, subtitle and explainer live under it. */
  page: string;
  exportPath?: string;
  exportLabel?: string;
  children?: ReactNode;
}) {
  const { t } = useTranslation(['bacen']);
  return (
    <>
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t(`${page}.title`)}</h1>
        <p className="text-sm text-muted-foreground">{t(`${page}.subtitle`)}</p>
      </header>
      <ExplainerCard i18nKey={`${page}.explainer`} />
      {exportPath ? (
        <div className="flex flex-wrap items-center justify-end gap-3">
          {children}
          <ExportButton path={exportPath} label={exportLabel} />
        </div>
      ) : (
        children
      )}
    </>
  );
}

export function Figure({
  label,
  value,
  href,
  second,
  testId,
}: {
  label: string;
  value: string;
  href?: string;
  second?: string;
  testId?: string;
}) {
  return (
    <div className="rounded-md border bg-muted/30 p-3" data-figure={testId}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-base font-semibold tabular-nums text-foreground">
        {href ? (
          <Link to={href} className="text-primary underline-offset-2 hover:underline">
            {value}
          </Link>
        ) : (
          value
        )}
      </div>
      {second ? <div className="mt-0.5 text-xs text-muted-foreground">{second}</div> : null}
    </div>
  );
}

/** One quantile ladder row: a label and the five BRL columns, or dashes. */
export function LadderRow({
  label,
  summary,
  currency,
  format,
}: {
  label: string;
  summary: QuantileSummary | null;
  currency: Intl.NumberFormat;
  format?: (value: number) => string;
}) {
  const render = format ?? ((value: number) => currency.format(value));
  return (
    <tr className="border-t" data-ladder={label}>
      <td className="px-3 py-2 text-foreground">{label}</td>
      {QUANTILES.map((q) => (
        <td key={q} className="px-3 py-2 text-right tabular-nums text-foreground">
          {summary ? render(summary.quantiles[q] ?? 0) : '—'}
        </td>
      ))}
      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
        {summary ? summary.n.toLocaleString() : '—'}
      </td>
    </tr>
  );
}

export function LadderHead({ nLabel }: { nLabel: string }) {
  return (
    <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
      <tr>
        <th className="px-3 py-2 font-medium" />
        {QUANTILES.map((q) => (
          <th key={q} className="px-3 py-2 text-right font-medium">
            {q}
          </th>
        ))}
        <th className="px-3 py-2 text-right font-medium">{nLabel}</th>
      </tr>
    </thead>
  );
}

export function Select({
  id,
  label,
  any,
  value,
  onChange,
  options,
  render,
}: {
  id: string;
  label: string;
  any: string;
  value: string;
  onChange: (value: string) => void;
  options?: BacenFilterOption[];
  render?: (option: BacenFilterOption) => string;
}) {
  const { number } = useFormats();
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs font-medium text-foreground">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="max-w-[13rem] rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
      >
        <option value="">{any}</option>
        {(options ?? []).map((option) => (
          <option key={option.value} value={option.value}>
            {render ? render(option) : option.value} ({number.format(option.rows)})
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * A natural person as BACEN publishes it: the name and the masked CPF, plain
 * text. Never a link (persons have no profile, by decision); a company links
 * to its profile by its full CNPJ.
 */
export function SubjectCell({
  name,
  subjectType,
  maskedCpf,
  cnpj,
}: {
  name: string;
  subjectType: string;
  maskedCpf: string | null;
  cnpj: string | null;
}) {
  if (subjectType === 'company' && cnpj && cnpj.length === CNPJ_DIGITS) {
    return (
      <Link
        to={`/bacen/e/${cnpj}`}
        className="line-clamp-2 text-primary underline-offset-4 hover:underline"
      >
        {name}
      </Link>
    );
  }
  return (
    <span className="text-foreground">
      <span className="line-clamp-2">{name}</span>
      {maskedCpf ? (
        <span className="block text-xs tabular-nums text-muted-foreground">{maskedCpf}</span>
      ) : null}
    </span>
  );
}
